// ─── Month names ─────────────────────────────────────────────────────────────
export const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

export const parseBoolFlag = (val, defaultVal = false) => {
  if (val === undefined || val === null || val === "") return defaultVal;
  return val === true || val === 1 || String(val).toLowerCase() === "true" || String(val) === "1";
};

// ─── Resolve whether Sunday is included for an employee ─────────────────────
export const isSundayIncludeForEmployee = (payrollParams, employee = null, departmentListOrObj = null) => {
  if (!payrollParams) return false;

  // 1. Department-wise Sunday Include check
  const isDeptWise = !!(payrollParams.IsSundayDeptWise || payrollParams.SundayIncludeDeptwise);
  if (isDeptWise) {
    let deptObj = null;
    if (departmentListOrObj && !Array.isArray(departmentListOrObj)) {
      deptObj = departmentListOrObj;
    } else if (Array.isArray(departmentListOrObj) && employee) {
      const deptId = employee.F_DepartmentMaster || employee.Dept_F_DepartmentMaster;
      const deptName = String(employee.DepartmentName || employee.Department || "").trim().toLowerCase();
      deptObj = departmentListOrObj.find(d => 
        (deptId && String(d.Id) === String(deptId)) ||
        (d.Name && String(d.Name).trim().toLowerCase() === deptName)
      );
    }
    if (deptObj) {
      return !!(deptObj.IsSundayInclude === true || deptObj.IsSundayInclude === "true" || deptObj.IsSundayInclude === 1);
    }
    // If employee has no department or department not found, fallback to global parameter
    return !!(payrollParams.IsSundayInclude || payrollParams.SundayInclude);
  }

  // 2. Global Sunday Include check
  return !!(payrollParams.IsSundayInclude || payrollParams.SundayInclude);
};

// ─── Resolve whether Direct Wages is enabled for an employee ────────────────
export const isDirectWagesForEmployee = (payrollParams, employee = null, departmentListOrObj = null) => {
  if (!payrollParams) return false;

  // 1. Department-wise Direct Wages check
  const isDeptWise = !!(payrollParams.IsDirectWagesDeptWise || payrollParams.DirectWagesDepartmentwise);
  if (isDeptWise) {
    let deptObj = null;
    if (departmentListOrObj && !Array.isArray(departmentListOrObj)) {
      deptObj = departmentListOrObj;
    } else if (Array.isArray(departmentListOrObj) && employee) {
      const deptId = employee.F_DepartmentMaster || employee.Dept_F_DepartmentMaster;
      const deptName = String(employee.DepartmentName || employee.Department || "").trim().toLowerCase();
      deptObj = departmentListOrObj.find(d => 
        (deptId && String(d.Id) === String(deptId)) ||
        (d.Name && String(d.Name).trim().toLowerCase() === deptName)
      );
    }
    if (deptObj) {
      return !!(deptObj.IsDirectWages === true || deptObj.IsDirectWages === "true" || deptObj.IsDirectWages === 1);
    }
    // Fallback: If employee has no department or department not found, check global parameter
    return !!(payrollParams.IsDirectWagesEntry || payrollParams.DirectWagesEntry);
  }

  // 2. Global Direct Wages check
  return !!(payrollParams.IsDirectWagesEntry || payrollParams.DirectWagesEntry);
};

// ─── 26 Days Working Check (Global or Employee-Wise) ─────────────────────────
export const is26DaysForEmployee = (payrollParams, employee) => {
  if (!payrollParams) return false;
  const isEmpWise = !!(payrollParams.Is26DaysEmployeeWise || payrollParams.Days26WorkingEmployeeWise);
  if (isEmpWise) {
    return !!(employee?.Is26DaysWorking || employee?.Is26Days || employee?.Days26Working);
  }
  const isGlobal = !!(payrollParams.Is26DaysWorking || payrollParams.Days26Working);
  if (isGlobal) {
    return true;
  }
  return false;
};

// ─── Month days calculator ──────────────────────────────────────────────────
export const getCalculatedMonthDays = (monthName, year, payrollParams, employeeWeeklyHoliday, departmentListOrObj = null, employee = null) => {
  // If 26 Days Working applies to this employee, MonthDays is fixed at 26
  if (is26DaysForEmployee(payrollParams, employee)) {
    return 26;
  }

  // If FixMonthDays is set in Payroll Parameter (> 0), use it directly
  const fixDays = parseFloat(payrollParams?.FixMonthDays);
  if (!isNaN(fixDays) && fixDays > 0) {
    return fixDays;
  }

  if (!monthName || !year) return 31;
  const monthIndex = typeof monthName === "number"
    ? monthName
    : MONTH_NAMES.findIndex(m => m.toLowerCase() === String(monthName || "").trim().toLowerCase());
  if (monthIndex === -1) return 31;

  const yNum = parseInt(year, 10) || new Date().getFullYear();
  // Calculate calendar days in that month
  const calendarDays = new Date(yNum, monthIndex + 1, 0).getDate();
  let calculatedMonthDays = calendarDays;

  // Determine whether Sunday is included (globally or department-wise)
  const isSundayIncluded = isSundayIncludeForEmployee(payrollParams, employee, departmentListOrObj);

  // Adjust if isSundayIncluded is false
  if (!isSundayIncluded) {
    const dayMap = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6
    };
    
    let holidayDayName = "";
    const empWh = String(employeeWeeklyHoliday || "").trim().toLowerCase();
    if (empWh && dayMap[empWh] !== undefined) {
      holidayDayName = empWh;
    } else {
      const firmWh = String(payrollParams?.F_FirmHoliday || payrollParams?.FirmHoliday || "").trim().toLowerCase();
      if (firmWh && dayMap[firmWh] !== undefined) {
        holidayDayName = firmWh;
      } else {
        holidayDayName = "sunday";
      }
    }

    const targetDay = dayMap[holidayDayName];
    if (targetDay !== undefined) {
      let count = 0;
      for (let d = 1; d <= calendarDays; d++) {
        const date = new Date(yNum, monthIndex, d);
        if (date.getDay() === targetDay) {
          count++;
        }
      }
      calculatedMonthDays = calendarDays - count;
    }
  }

  return calculatedMonthDays;
};

// ─── Resolve whether Employee is Daily Wage ─────────────────────────────────
export const isDailyWageEmployee = (employeeOrRow = null, salaryEntry = null) => {
  if (salaryEntry && (salaryEntry.F_BasicTypeMaster === 2 || String(salaryEntry.F_BasicTypeMaster) === "2")) {
    return true;
  }
  if (employeeOrRow) {
    if (employeeOrRow.F_BasicTypeMaster === 2 || String(employeeOrRow.F_BasicTypeMaster) === "2") {
      return true;
    }
    const typeName = String(employeeOrRow.BasicTypeName || employeeOrRow.BasicType || "").trim().toLowerCase();
    if (typeName.includes("day") || typeName.includes("daily")) {
      return true;
    }
    if (employeeOrRow.SalaryJson) {
      try {
        const salList = typeof employeeOrRow.SalaryJson === "string" ? JSON.parse(employeeOrRow.SalaryJson) : employeeOrRow.SalaryJson;
        if (Array.isArray(salList)) {
          const entry = salList.find(s => s.F_BasicTypeMaster === 2 || String(s.F_BasicTypeMaster) === "2");
          if (entry) return true;
        }
      } catch {}
    }
  }
  return false;
};

// ─── Weekly Holiday Count calculator ────────────────────────────────────────
export const getWeeklyHolidayCount = (monthName, year, payrollParams, employeeWeeklyHoliday, departmentListOrObj = null, employee = null, salaryEntry = null) => {
  if (!monthName || !year) return 0;
  const monthIndex = MONTH_NAMES.indexOf(monthName);
  if (monthIndex === -1) return 0;

  // In 26 Days Working mode, the standard month is 26 working days (Sundays are not extra paid days)
  if (is26DaysForEmployee(payrollParams, employee)) {
    return 0;
  }

  const isNoWHForDaily = !!(payrollParams?.IsNoWeeklyHolidayDailyWages || payrollParams?.NoWeeklyHolidayForDailyWages);
  if (isNoWHForDaily && isDailyWageEmployee(employee, salaryEntry)) {
    return 0;
  }

  const isSundayIncluded = isSundayIncludeForEmployee(payrollParams, employee, departmentListOrObj);

  if (!isSundayIncluded) {
    const dayMap = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6
    };
    
    let holidayDayName = "";
    const empWh = String(employeeWeeklyHoliday || "").trim().toLowerCase();
    if (empWh && dayMap[empWh] !== undefined) {
      holidayDayName = empWh;
    } else {
      const firmWh = String(payrollParams?.F_FirmHoliday || payrollParams?.FirmHoliday || "").trim().toLowerCase();
      if (firmWh && dayMap[firmWh] !== undefined) {
        holidayDayName = firmWh;
      } else {
        holidayDayName = "sunday";
      }
    }

    const targetDay = dayMap[holidayDayName];
    if (targetDay !== undefined) {
      const calendarDays = new Date(year, monthIndex + 1, 0).getDate();
      let count = 0;
      for (let d = 1; d <= calendarDays; d++) {
        const date = new Date(year, monthIndex, d);
        if (date.getDay() === targetDay) {
          count++;
        }
      }
      return count;
    }
  }
  return 0;
};

// ─── Column Definitions (full, all toggleable) ────────────────────────────────
export const COLS = [
  // Employee Info
  { key: "F_EmployeeMaster", label: "Employee *",      type: "select",  optKey: "EmployeeMaster", w: 180 },
  { key: "EmployeeName",     label: "Emp. Name",       type: "text",    w: 150, readOnly: true },
  { key: "FatherName",       label: "Father Name",     type: "text",    w: 130, readOnly: true },
  { key: "Department",       label: "Department",      type: "text",    w: 130, readOnly: true },
  { key: "JoinDate",         label: "Join Date",       type: "text",    w: 110, readOnly: true },
  { key: "BasicSalary",      label: "Basic Salary",    type: "number",  w: 100 },
  { key: "BasicTypeName",    label: "Basic Type",      type: "text",    w: 90,  readOnly: true },
  // Attendance
  { key: "MonthDays",        label: "Month Days",      type: "number",  w: 85 },
  { key: "WorkingDays",      label: "Working Days",    type: "number",  w: 90 },
  { key: "WorkingHours",     label: "Working Hrs",     type: "number",  w: 90, paramKey: "IsHourlyPayment" },
  { key: "WorkingMinutes",   label: "Working Mins",    type: "number",  w: 90, paramKey: "IsHourlyPayment" },
  { key: "LeaveWages",       label: "Leave Wages",     type: "number",  w: 85 },
  { key: "FestivalHoliday",  label: "Festival Hol.",   type: "number",  w: 90 },
  { key: "WeeklyHoliday",    label: "Weekly Hol.",     type: "number",  w: 85 },
  { key: "CL",               label: "CL",              type: "number",  w: 60 },
  { key: "ML",               label: "ML",              type: "number",  w: 60 },
  { key: "PaidDay",          label: "Paid Days",       type: "number",  w: 75, readOnly: true },
  { key: "OvertimeHours",    label: "OT Hours",        type: "number",  w: 80, paramKey: "IsOvertime" },
  // Earnings (all conditional)
  { key: "Arrear",           label: "Arrear",          type: "number",  w: 80, paramKey: "IsArrearApplicable" },
  { key: "AdvanceBonus",     label: "Adv. Bonus",      type: "number",  w: 85, paramKey: "IsAdvanceBonus" },
  { key: "ExtraPay",         label: "Extra Pay",       type: "number",  w: 80, paramKey: "IsExtraPay", labelParamKey: "ExtraPayCaption" },
  { key: "ExtraPay1",        label: "Extra Pay 1",     type: "number",  w: 85, paramKey: "IsExtraPay1", labelParamKey: "ExtraPay1Caption" },
  { key: "OverTime",         label: "OT Amount",       type: "number",  w: 85, readOnly: true, paramKey: "IsOvertime" },
  
  // 10 Allowance columns
  { key: "OriginalAllowance1", label: "Allowance 1", type: "number", w: 85, isAllowance: true, idx: 1 },
  { key: "OriginalAllowance2", label: "Allowance 2", type: "number", w: 85, isAllowance: true, idx: 2 },
  { key: "OriginalAllowance3", label: "Allowance 3", type: "number", w: 85, isAllowance: true, idx: 3 },
  { key: "OriginalAllowance4", label: "Allowance 4", type: "number", w: 85, isAllowance: true, idx: 4 },
  { key: "OriginalAllowance5", label: "Allowance 5", type: "number", w: 85, isAllowance: true, idx: 5 },
  { key: "OriginalAllowance6", label: "Allowance 6", type: "number", w: 85, isAllowance: true, idx: 6 },
  { key: "OriginalAllowance7", label: "Allowance 7", type: "number", w: 85, isAllowance: true, idx: 7 },
  { key: "OriginalAllowance8", label: "Allowance 8", type: "number", w: 85, isAllowance: true, idx: 8 },
  { key: "OriginalAllowance9", label: "Allowance 9", type: "number", w: 85, isAllowance: true, idx: 9 },
  { key: "OriginalAllowance10", label: "Allowance 10", type: "number", w: 85, isAllowance: true, idx: 10 },

  { key: "TotalAllowances",  label: "Allowances",      type: "number",  w: 90, readOnly: true },
  { key: "TotalDeductions",  label: "Ded.(Other)",     type: "number",  w: 90, readOnly: true },
  // Deductions (conditional)
  { key: "Recovery",         label: "Recovery",        type: "number",  w: 80, paramKey: "IsRecovery" },
  { key: "Advance",          label: "Advance",         type: "number",  w: 80, paramKey: "IsAdvance" },
  { key: "TDS",              label: "TDS",             type: "number",  w: 70, paramKey: "IsTDS" },

  // 10 Deduction columns
  { key: "OriginalDeduction1", label: "Deduction 1", type: "number", w: 85, isDeduction: true, idx: 1 },
  { key: "OriginalDeduction2", label: "Deduction 2", type: "number", w: 85, isDeduction: true, idx: 2 },
  { key: "OriginalDeduction3", label: "Deduction 3", type: "number", w: 85, isDeduction: true, idx: 3 },
  { key: "OriginalDeduction4", label: "Deduction 4", type: "number", w: 85, isDeduction: true, idx: 4 },
  { key: "OriginalDeduction5", label: "Deduction 5", type: "number", w: 85, isDeduction: true, idx: 5 },
  { key: "OriginalDeduction6", label: "Deduction 6", type: "number", w: 85, isDeduction: true, idx: 6 },
  { key: "OriginalDeduction7", label: "Deduction 7", type: "number", w: 85, isDeduction: true, idx: 7 },
  { key: "OriginalDeduction8", label: "Deduction 8", type: "number", w: 85, isDeduction: true, idx: 8 },
  { key: "OriginalDeduction9", label: "Deduction 9", type: "number", w: 85, isDeduction: true, idx: 9 },
  { key: "OriginalDeduction10", label: "Deduction 10", type: "number", w: 85, isDeduction: true, idx: 10 },

  { key: "PFRefund",         label: "PF Refund",       type: "number",  w: 80, paramKey: "IsPFAdvRefund" },
  { key: "ArrearPF",         label: "Arrear PF",       type: "number",  w: 80, paramKey: "IsPF" },
  { key: "ArrERPF",          label: "Arr.ERPF",        type: "number",  w: 80, paramKey: "IsPF" },
  { key: "ArrPens",          label: "Arr.Pens.",       type: "number",  w: 80, paramKey: "IsPF" },
  { key: "PFReason",         label: "PF Reason",       type: "text",    w: 120, paramKey: "IsPF" },
  { key: "PFLast",           label: "PF Last",         type: "text",    w: 80,  paramKey: "IsPF" },
  { key: "ESIReason",        label: "ESI Reason",      type: "text",    w: 120, paramKey: "IsESI" },
  { key: "ESILast",          label: "ESI Last",        type: "text",    w: 85,  paramKey: "IsESI" },
  { key: "LastWorking",      label: "Last Working",    type: "text",    w: 85 },
  // PF / ESI (conditional)
  { key: "PF",               label: "PF",              type: "number",  w: 75,  readOnly: true, paramKey: "IsPF" },
  { key: "EmplrPF",          label: "Emplr PF",        type: "number",  w: 80,  readOnly: true, paramKey: "IsPF" },
  { key: "Pension",          label: "Pension",         type: "number",  w: 80,  readOnly: true, paramKey: "IsPF" },

  { key: "AC22",             label: "AC22",            type: "number",  w: 70,  readOnly: true, paramKey: "IsPF" },
  { key: "ESI",              label: "ESI",             type: "number",  w: 70,  readOnly: true, paramKey: "IsESI" },
  { key: "EmplrESI",         label: "Emplr ESI",       type: "number",  w: 80,  readOnly: true, paramKey: "IsESI" },
  { key: "Bonus",            label: "Bonus",           type: "number",  w: 75,  readOnly: true, paramKey: "IsBonusParameter" },
  { key: "Gratuity",         label: "Gratuity",        type: "number",  w: 80,  readOnly: true, paramKey: "IsGratuity" },
  // Leave Balance
  { key: "PL_Bal",           label: "PL Bal",          type: "number",  w: 65 },
  { key: "CL_Bal",           label: "CL Bal",          type: "number",  w: 65 },
  { key: "ML_Bal",           label: "ML Bal",          type: "number",  w: 65 },
  { key: "PL_Enjoy",         label: "PL Enjoy",        type: "number",  w: 70 },
  { key: "CL_Enjoy",         label: "CL Enjoy",        type: "number",  w: 70 },
  { key: "ML_Enjoy",         label: "ML Enjoy",        type: "number",  w: 70 },
  // Leave Dates
  { key: "LeaveWithWages",   label: "Leave w/Wages",   type: "number",  w: 90 },
  { key: "LeaveFrom1",       label: "Lv.From1",        type: "number",  w: 75 },
  { key: "LeaveTo1",         label: "Lv.To1",          type: "number",  w: 70 },
  { key: "LeaveFrom2",       label: "Lv.From2",        type: "number",  w: 75 },
  { key: "LeaveTo2",         label: "Lv.To2",          type: "number",  w: 70 },
  { key: "LeaveFrom3",       label: "Lv.From3",        type: "number",  w: 75 },
  { key: "LeaveTo3",         label: "Lv.To3",          type: "number",  w: 70 },
  // Absent
  { key: "AbsentDays",       label: "Absent Days",     type: "number",  w: 80 },
  { key: "AbsentFrom1",      label: "Ab.From1",        type: "number",  w: 75 },
  { key: "AbsentTo1",        label: "Ab.To1",          type: "number",  w: 70 },
  { key: "AbsentFrom2",      label: "Ab.From2",        type: "number",  w: 75 },
  { key: "AbsentTo2",        label: "Ab.To2",          type: "number",  w: 70 },
  { key: "AbsentFrom3",      label: "Ab.From3",        type: "number",  w: 75 },
  { key: "AbsentTo3",        label: "Ab.To3",          type: "number",  w: 70 },
  // Summary
  { key: "Wages",            label: "Wages",           type: "number",  w: 85,  readOnly: true },
  { key: "GrossAmount",      label: "Gross Amt",       type: "number",  w: 95,  readOnly: true },
  { key: "TotalDeduction",   label: "Total Deduct.",   type: "number",  w: 100, readOnly: true },
  { key: "NetAmount",        label: "Net Amount",      type: "number",  w: 95,  readOnly: true }
];

// Group bands for header row
export const COL_GROUPS = [
  { label: "Employee Info",  startKey: "F_EmployeeMaster", endKey: "BasicTypeName",   color: "#1565c0" },
  { label: "Attendance",     startKey: "MonthDays",        endKey: "OvertimeHours",   color: "#1b5e20" },
  { label: "Earnings",       startKey: "Arrear",           endKey: "TotalDeductions", color: "#e65100" },
  { label: "Deductions",     startKey: "Recovery",         endKey: "PFLast",          color: "#b71c1c" },
  { label: "PF / ESI",       startKey: "PF",               endKey: "Gratuity",        color: "#006064" },
  { label: "Leave Balance",  startKey: "PL_Bal",           endKey: "ML_Enjoy",        color: "#33691e" },
  { label: "Leave Dates",    startKey: "LeaveWithWages",   endKey: "LeaveTo3",        color: "#01579b" },
  { label: "Absent",         startKey: "AbsentDays",       endKey: "AbsentTo3",       color: "#880e4f" },
  { label: "Summary",        startKey: "Wages",            endKey: "NetAmount",       color: "#37474f" },
];

export const visibleKeys = new Set(COLS.map(c => c.key));

// ─── Numeric helper ───────────────────────────────────────────────────────────
export const n = (v) => parseFloat(v) || 0;

export const formatDateForSave = (dateValue) => {
  if (!dateValue) return "";
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  const dateObj = new Date(dateValue);
  if (isNaN(dateObj.getTime())) return "";
  return dateObj.toISOString().split('T')[0];
};

// ─── Empty row factory ────────────────────────────────────────────────────────
export const emptyRow = () => {
  const base = {
    Id: 0,
    F_EmployeeMaster: "", EmployeeName: "", FatherName: "", Department: "", JoinDate: "",
    BasicSalary: 0, BasicTypeName: "",
    MonthDays: 31,
    WorkingDays: 0, WorkingHours: 0, WorkingMinutes: 0, LeaveWages: 0, FestivalHoliday: 0,
    WeeklyHoliday: 0, CL: 0, ML: 0, PaidDay: 0, OvertimeHours: 0,
    Arrear: 0, AdvanceBonus: 0, ExtraPay: 0, ExtraPay1: 0, OverTime: 0,
    TotalAllowances: 0, TotalDeductions: 0,
    Recovery: 0, Advance: 0, TDS: 0, PFRefund: 0, ArrearPF: 0, ArrERPF: 0, ArrPens: 0,
    PFReason: "", PFLast: "",
    PF: 0, EmplrPF: 0, Pension: 0, AdminCharges: 0, AC21: 0, AC22: 0,
    ESI: 0, EmplrESI: 0, Bonus: 0, Gratuity: 0,
    PFApplicable: false, ESIApplicable: false,
    PL_Bal: 0, CL_Bal: 0, ML_Bal: 0, PL_Enjoy: 0, CL_Enjoy: 0, ML_Enjoy: 0,
    LeaveWithWages: 0,
    LeaveFrom1: "", LeaveTo1: "", LeaveFrom2: "", LeaveTo2: "", LeaveFrom3: "", LeaveTo3: "",
    AbsentDays: 0, AbsentFrom1: "", AbsentTo1: "", AbsentFrom2: "", AbsentTo2: "",
    AbsentFrom3: "", AbsentTo3: "",
    Wages: 0, GrossAmount: 0, TotalDeduction: 0, NetAmount: 0,
    Original_BasicSalary: 0, Calculated_BasicSalary: 0,
  };
  for (let i = 1; i <= 10; i++) {
    base[`Allowance${i}Name`] = "";
    base[`OriginalAllowance${i}`] = 0;
    base[`CalculatedAllowance${i}`] = 0;
    base[`Deduction${i}Name`] = "";
    base[`OriginalDeduction${i}`] = 0;
    base[`CalculatedDeduction${i}`] = 0;
  }
  return base;
};

// Helper to parse date into Date object (handles ISO YYYY-MM-DD, DD-MM-YYYY, DD/MM/YYYY, etc.)
export const parseEffDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return isNaN(dateVal.getTime()) ? null : dateVal;
  const s = String(dateVal).split("T")[0].split(" ")[0].trim();
  if (!s) return null;
  // YYYY-MM-DD or YYYY/MM/DD
  const m1 = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m1) {
    const y = parseInt(m1[1], 10);
    const m = parseInt(m1[2], 10) - 1;
    const d = parseInt(m1[3], 10);
    const dateObj = new Date(y, m, d);
    return isNaN(dateObj.getTime()) ? null : dateObj;
  }
  // DD-MM-YYYY or DD/MM/YYYY
  const m2 = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m2) {
    const d = parseInt(m2[1], 10);
    const m = parseInt(m2[2], 10) - 1;
    const y = parseInt(m2[3], 10);
    const dateObj = new Date(y, m, d);
    return isNaN(dateObj.getTime()) ? null : dateObj;
  }
  const direct = new Date(s);
  return isNaN(direct.getTime()) ? null : direct;
};

// Pick latest allowance entry per master whose EffectiveFrom <= payroll period (with fallback to latest entry)
export const getEffectiveAllowances = (allowanceList, selectedMonth, selectedYear) => {
  if (!allowanceList || !Array.isArray(allowanceList) || allowanceList.length === 0) {
    return [];
  }

  const periodMonth = typeof selectedMonth === "number"
    ? selectedMonth
    : (MONTH_NAMES.indexOf(selectedMonth) >= 0 ? MONTH_NAMES.indexOf(selectedMonth) : -1);
  const periodYear = parseInt(selectedYear, 10) || 0;

  // Group all entries by Allowance Master ID
  const groups = {};
  allowanceList.forEach((a, idx) => {
    if (!a) return;
    const masterId = a.F_AllowanceMaster || a.AllowanceMasterId || a.Id;
    if (!masterId || masterId === "none") return;
    const key = String(masterId);
    if (!groups[key]) groups[key] = [];
    groups[key].push({ ...a, _origIndex: idx });
  });

  const sortLatestFirst = (list) => {
    return [...list].sort((a, b) => {
      const dateA = a.EffectiveFrom || a.EffectiveDate || a.Date || "";
      const dateB = b.EffectiveFrom || b.EffectiveDate || b.Date || "";
      const dA = parseEffDate(dateA);
      const dB = parseEffDate(dateB);
      const tA = dA ? dA.getTime() : 0;
      const tB = dB ? dB.getTime() : 0;
      if (tA !== tB) {
        return tB - tA; // Latest date first
      }
      return (b._origIndex ?? 0) - (a._origIndex ?? 0); // Highest index first
    });
  };

  const result = [];

  Object.values(groups).forEach(entries => {
    if (entries.length === 0) return;

    if (periodYear > 0 && periodMonth >= 0) {
      const eligible = entries.filter(e => {
        const dateVal = e.EffectiveFrom || e.EffectiveDate || e.Date || "";
        const d = parseEffDate(dateVal);
        if (!d) return true;
        const y = d.getFullYear();
        const m = d.getMonth();
        return (y < periodYear) || (y === periodYear && m <= periodMonth);
      });

      if (eligible.length > 0) {
        const sortedEligible = sortLatestFirst(eligible);
        result.push(sortedEligible[0]);
        return;
      }
    }

    // Fallback: Pick latest version by EffectiveFrom date / last entry
    const sortedAll = sortLatestFirst(entries);
    result.push(sortedAll[0]);
  });

  return result;
};

export const computeSingleAllowance = (a, monthDays, paidDays, basicSalary) => {
  if (!a) return 0;
  if (a.IsApplicable === false || a.IsApplicable === "false" || a.IsApplicable === 0 || a.IsApplicable === "0") return 0;
  let base = parseFloat(a.Amount) || 0;
  const calc = String(a.Calculation || "").toUpperCase().trim();
  if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
    base = (base / 100) * (parseFloat(basicSalary) || 0);
  } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
    return Math.round(base * (parseFloat(paidDays) || 0) * 100) / 100;
  }
  const isWorkDays = a.IsWorkingDaysApply === true || a.IsWorkingDaysApply === "true" || a.IsWorkingDaysApply === 1;
  const mDays = parseFloat(monthDays) || 0;
  const pDays = parseFloat(paidDays) || 0;
  if (isWorkDays && mDays > 0) {
    base = (base / mDays) * pDays;
  }
  return Math.round(base * 100) / 100;
};

export const computeAllowancesTotal = (allowanceList, monthDays, paidDays, basicSalary, selectedMonth, selectedYear) => {
  const effective = getEffectiveAllowances(allowanceList, selectedMonth, selectedYear);
  let total = 0;
  effective.forEach(a => {
    total += computeSingleAllowance(a, monthDays, paidDays, basicSalary);
  });
  return Math.round(total * 100) / 100;
};

export const computePFAllowancesAmount = (allowanceList, monthDays, paidDays, basicSalary, selectedMonth, selectedYear) => {
  const effective = getEffectiveAllowances(allowanceList, selectedMonth, selectedYear);
  let total = 0;
  effective.forEach(a => {
    const isPF = parseBoolFlag(a.IsPFApply, false);
    if (isPF) {
      total += computeSingleAllowance(a, monthDays, paidDays, basicSalary);
    }
  });
  return Math.round(total * 100) / 100;
};

export const computeESIAllowancesAmount = (allowanceList, monthDays, paidDays, basicSalary, selectedMonth, selectedYear) => {
  const effective = getEffectiveAllowances(allowanceList, selectedMonth, selectedYear);
  let total = 0;
  effective.forEach(a => {
    const name = (a.AllowanceName || a.Name || a.AllowanceMasterName || "").trim().toUpperCase();
    const isHRA = name === "HRA" || name === "HOUSE RENT ALLOWANCE";
    // Statutorily under Section 2(22) ESI Act, HRA is always part of wages for ESI
    const isESI = isHRA || parseBoolFlag(a.IsESIApply, false);
    if (isESI) {
      total += computeSingleAllowance(a, monthDays, paidDays, basicSalary);
    }
  });
  return Math.round(total * 100) / 100;
};

// Pick latest deduction entry per master whose EffectiveFrom <= payroll period (with fallback to latest entry)
export const getEffectiveDeductions = (deductionList, selectedMonth, selectedYear) => {
  if (!deductionList || !Array.isArray(deductionList) || deductionList.length === 0) {
    return [];
  }

  const periodMonth = typeof selectedMonth === "number"
    ? selectedMonth
    : (MONTH_NAMES.indexOf(selectedMonth) >= 0 ? MONTH_NAMES.indexOf(selectedMonth) : -1);
  const periodYear = parseInt(selectedYear, 10) || 0;

  // Group all entries by Deduction Master ID
  const groups = {};
  deductionList.forEach((d, idx) => {
    if (!d) return;
    const masterId = d.F_DeductionMaster || d.DeductionMasterId || d.Id;
    if (!masterId || masterId === "none") return;
    const key = String(masterId);
    if (!groups[key]) groups[key] = [];
    groups[key].push({ ...d, _origIndex: idx });
  });

  const sortLatestFirst = (list) => {
    return [...list].sort((a, b) => {
      const dateA = a.EffectiveFrom || a.EffectiveDate || a.Date || "";
      const dateB = b.EffectiveFrom || b.EffectiveDate || b.Date || "";
      const dA = parseEffDate(dateA);
      const dB = parseEffDate(dateB);
      const tA = dA ? dA.getTime() : 0;
      const tB = dB ? dB.getTime() : 0;
      if (tA !== tB) {
        return tB - tA; // Latest date first
      }
      return (b._origIndex ?? 0) - (a._origIndex ?? 0); // Highest index first
    });
  };

  const result = [];

  Object.values(groups).forEach(entries => {
    if (entries.length === 0) return;

    if (periodYear > 0 && periodMonth >= 0) {
      const eligible = entries.filter(e => {
        const dateVal = e.EffectiveFrom || e.EffectiveDate || e.Date || "";
        const dt = parseEffDate(dateVal);
        if (!dt) return true;
        const y = dt.getFullYear();
        const m = dt.getMonth();
        return (y < periodYear) || (y === periodYear && m <= periodMonth);
      });

      if (eligible.length > 0) {
        const sortedEligible = sortLatestFirst(eligible);
        result.push(sortedEligible[0]);
        return;
      }
    }

    // Fallback: Pick latest version by EffectiveFrom date / last entry
    const sortedAll = sortLatestFirst(entries);
    result.push(sortedAll[0]);
  });

  return result;
};

export const computeSingleDeduction = (d, monthDays, paidDays, basicSalary) => {
  if (!d) return 0;
  if (d.IsApplicable === false || d.IsApplicable === "false" || d.IsApplicable === 0 || d.IsApplicable === "0") return 0;
  let base = parseFloat(d.Amount) || 0;
  const calc = String(d.Calculation || "").toUpperCase().trim();
  if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
    base = (base / 100) * (parseFloat(basicSalary) || 0);
  } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
    return Math.round(base * (parseFloat(paidDays) || 0) * 100) / 100;
  }
  const isWorkDays = d.IsWorkingDaysApply === true || d.IsWorkingDaysApply === "true" || d.IsWorkingDaysApply === 1;
  const mDays = parseFloat(monthDays) || 0;
  const pDays = parseFloat(paidDays) || 0;
  if (isWorkDays && mDays > 0) {
    base = (base / mDays) * pDays;
  }
  return Math.round(base * 100) / 100;
};

export const computeDeductionsTotal = (deductionList, monthDays, paidDays, basicSalary, selectedMonth, selectedYear) => {
  const effective = getEffectiveDeductions(deductionList, selectedMonth, selectedYear);
  let total = 0;
  effective.forEach(d => {
    total += computeSingleDeduction(d, monthDays, paidDays, basicSalary);
  });
  return Math.round(total * 100) / 100;
};

// Pick latest SalaryJson entry whose EffectiveDate <= selected month/year (with master employee fallback)
export const getEffectiveSalary = (salaryList, selectedMonth, selectedYear, emp = null) => {
  const periodMonth = typeof selectedMonth === "number"
    ? selectedMonth
    : MONTH_NAMES.findIndex(m => m.toLowerCase() === String(selectedMonth || "").trim().toLowerCase());
  const periodYear = parseInt(selectedYear, 10) || new Date().getFullYear();

  const getEmpFallback = () => {
    if (!emp) return null;
    const bSal = parseFloat(emp.BasicSalary || emp.Sal_BasicSalary || emp.Basic || emp.RatePerDay || emp.Salary || 0);
    const bType = parseInt(emp.F_BasicTypeMaster || emp.BasicType || 1, 10) || 1;
    if (bSal > 0) {
      return { BasicSalary: bSal, F_BasicTypeMaster: bType, EffectiveDate: "" };
    }
    return null;
  };

  let list = salaryList;
  if (typeof list === "string") {
    try { list = JSON.parse(list); } catch { list = []; }
  }

  if (!list || !Array.isArray(list) || list.length === 0) {
    return getEmpFallback();
  }

  const validEntries = list.filter(s => s && (parseFloat(s.BasicSalary) > 0 || s.F_BasicTypeMaster));
  const poolToUse = validEntries.length > 0 ? validEntries : list;

  const eligible = poolToUse.filter(s => {
    const eff = parseEffDate(s.EffectiveDate || s.EffectiveFrom || s.Date);
    if (!eff) return true;
    const y = eff.getFullYear();
    const m = eff.getMonth();
    return (y < periodYear) || (y === periodYear && m <= periodMonth);
  });

  const pool = eligible.length > 0 ? eligible : poolToUse;

  const best = pool.reduce((acc, s) => {
    if (!acc) return s;
    const dA = parseEffDate(acc.EffectiveDate || acc.EffectiveFrom || acc.Date);
    const dB = parseEffDate(s.EffectiveDate || s.EffectiveFrom || s.Date);
    const tA = dA ? dA.getTime() : 0;
    const tB = dB ? dB.getTime() : 0;
    if (tB !== tA) return tB > tA ? s : acc;
    const salB = parseFloat(s.BasicSalary) || 0;
    const salA = parseFloat(acc.BasicSalary) || 0;
    if (salB > 0 && salA === 0) return s;
    return acc;
  }, null);

  if ((!best || !(parseFloat(best.BasicSalary) > 0)) && emp) {
    const fallback = getEmpFallback();
    if (fallback) {
      return {
        ...fallback,
        ...(best || {}),
        BasicSalary: fallback.BasicSalary,
        F_BasicTypeMaster: best?.F_BasicTypeMaster || fallback.F_BasicTypeMaster
      };
    }
  }

  return best || getEmpFallback();
};

// Working days based on payrollParams and employee
export const computeWorkingDays = (monthDays, payrollParams, employee = null) => {
  if (!payrollParams) return 26;
  if (is26DaysForEmployee(payrollParams, employee)) return 26;
  if (payrollParams.Is26DaysWorking) return 26;
  if (payrollParams.IsSundayInclude) return monthDays;
  return monthDays;
};

// Basic salary from salary entry + BasicType (with employee profile fallback)
export const computeBasic = (salaryEntry, workingDays, payrollParams, employee = null) => {
  const raw  = parseFloat(salaryEntry?.BasicSalary || employee?.BasicSalary || employee?.Sal_BasicSalary || employee?.Salary || 0) || 0;
  const type = parseInt(salaryEntry?.F_BasicTypeMaster || employee?.F_BasicTypeMaster || employee?.BasicType || 1, 10) || 1;
  if (type === 1) return raw;
  if (type === 2) return Math.round(raw * workingDays * 100) / 100;
  if (type === 3) {
    const stdHours = parseFloat(payrollParams?.WorkingHours) || 8;
    return Math.round(raw * workingDays * stdHours * 100) / 100;
  }
  return raw;
};

// Wages from paid days + BasicType (with employee profile fallback)
// WorkingHours & WorkingMinutes represent hours worked in addition to working days (days ke alawa extra hours)
export const computeWages = (salaryEntry, paidDays, monthDays, workingHours, workingMinutes, payrollParams, employee = null) => {
  const basicSalary = parseFloat(salaryEntry?.BasicSalary || employee?.BasicSalary || employee?.Sal_BasicSalary || employee?.Salary || 0) || 0;
  const type = parseInt(salaryEntry?.F_BasicTypeMaster || employee?.F_BasicTypeMaster || employee?.BasicType || 1, 10) || 1;
  const stdHours = parseFloat(payrollParams?.WorkingHours) || 8;
  const extraHours = (parseFloat(workingHours) || 0) + ((parseFloat(workingMinutes) || 0) / 60);

  // Monthly: BasicSalary * (PaidDays / MonthDays) + (hourly pay for extra hours if IsHourlyPayment)
  if (type === 1) { 
    if (monthDays === 0) return 0;
    const dayWages = (basicSalary * paidDays) / monthDays;
    const hourWages = (payrollParams?.IsHourlyPayment && extraHours > 0)
      ? ((basicSalary / monthDays / stdHours) * extraHours)
      : 0;
    const val = dayWages + hourWages;
    return payrollParams?.IsWagesRoundedOff ? Math.round(val) : Math.round(val * 100) / 100;
  }

  // Per Day: BasicSalary * PaidDays + (hourly pay for extra hours if IsHourlyPayment)
  if (type === 2) { 
    const dayWages = basicSalary * paidDays;
    const hourWages = (payrollParams?.IsHourlyPayment && extraHours > 0)
      ? ((basicSalary / stdHours) * extraHours)
      : 0;
    const val = dayWages + hourWages;
    return payrollParams?.IsWagesRoundedOff ? Math.round(val) : Math.round(val * 100) / 100;
  }

  // Hourly: BasicSalary * totalHours where totalHours = (PaidDays * stdHours) + extraHours
  if (type === 3) {
    const totalHours = (paidDays * stdHours) + extraHours;
    const val = basicSalary * totalHours;
    return payrollParams?.IsWagesRoundedOff ? Math.round(val) : Math.round(val * 100) / 100;
  }
  return payrollParams?.IsWagesRoundedOff ? Math.round(basicSalary) : basicSalary;
};

// Overtime using BasicType + payrollParams flags
// When IsOTOnGross is ON, calculate overtime based on Gross Wages (Basic + Master Allowances)
export const computeOvertime = (salaryEntry, otHours, monthDays, payrollParams, allowancesOrTotal = 0, selectedMonth = null, selectedYear = null, basicSalaryForAllowances = null) => {
  const isOTEnabled = !!(
    payrollParams?.IsOvertime || 
    payrollParams?.OverTime || 
    String(payrollParams?.IsOvertime).toLowerCase() === "true" || 
    payrollParams?.IsOvertime === 1
  );
  if (!payrollParams || !isOTEnabled) return 0;
  if (!salaryEntry) return 0;
  const hours = parseFloat(otHours) || 0;
  if (hours <= 0) return 0;
  const basicSalary = parseFloat(salaryEntry.BasicSalary) || 0;
  if (basicSalary <= 0) return 0;
  const type       = salaryEntry.F_BasicTypeMaster;

  const isOTOn26 = !!(
    payrollParams.IsOTOn26Days === true ||
    payrollParams.IsOTOn26Days === 1 ||
    String(payrollParams.IsOTOn26Days).toLowerCase() === "true" ||
    String(payrollParams.IsOTOn26Days) === "1" ||
    payrollParams.OvertimeOn26Days === true ||
    payrollParams.OvertimeOn26Days === 1 ||
    String(payrollParams.OvertimeOn26Days).toLowerCase() === "true" ||
    payrollParams.IsOvertimeOn26Days === true ||
    String(payrollParams.IsOvertimeOn26Days).toLowerCase() === "true"
  );
  const divisor    = isOTOn26 ? 26 : (parseFloat(monthDays) || 26);

  const isDoubleOT = !!(
    payrollParams.IsDoubleOT === true ||
    payrollParams.IsDoubleOT === 1 ||
    String(payrollParams.IsDoubleOT).toLowerCase() === "true" ||
    String(payrollParams.IsDoubleOT) === "1" ||
    payrollParams.DoubleOvertimeAmount === true ||
    payrollParams.DoubleOvertimeAmount === 1 ||
    String(payrollParams.DoubleOvertimeAmount).toLowerCase() === "true" ||
    payrollParams.IsDoubleOvertimeAmount === true ||
    String(payrollParams.IsDoubleOvertimeAmount).toLowerCase() === "true"
  );
  const multiplier = isDoubleOT ? 2 : 1;
  const stdHours   = parseFloat(payrollParams.WorkingHours) || 8;

  const isOTOnGross = !!(
    payrollParams.IsOTOnGross || 
    payrollParams.OvertimeOnGrossWages || 
    payrollParams.IsOvertimeOnGrossWages ||
    String(payrollParams.IsOTOnGross).toLowerCase() === "true" ||
    String(payrollParams.OvertimeOnGrossWages).toLowerCase() === "true"
  );

  let baseRate = basicSalary;

  if (isOTOnGross) {
    if (Array.isArray(allowancesOrTotal)) {
      const effectiveAllowances = getEffectiveAllowances(allowancesOrTotal, selectedMonth, selectedYear);
      const allowanceBase = basicSalaryForAllowances !== null && basicSalaryForAllowances !== undefined
        ? (parseFloat(basicSalaryForAllowances) || 0)
        : basicSalary;

      if (type === 2) {
        // Daily rate: Basic is daily. Calculate daily allowances rate
        let dailyAllowances = 0;
        effectiveAllowances.forEach(a => {
          if (!a || a.IsApplicable === false || a.IsApplicable === "false" || a.IsApplicable === 0 || a.IsApplicable === "0") return;
          const amt = parseFloat(a.Amount) || 0;
          const calc = String(a.Calculation || "").toUpperCase().trim();
          if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
            dailyAllowances += (amt / 100) * allowanceBase;
          } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
            dailyAllowances += amt;
          } else {
            // Fixed monthly amount -> daily rate is amt / divisor
            dailyAllowances += divisor > 0 ? (amt / divisor) : 0;
          }
        });
        baseRate = basicSalary + dailyAllowances;
      } else if (type === 3) {
        // Hourly rate: Basic is hourly. Calculate hourly allowances rate
        let hourlyAllowances = 0;
        effectiveAllowances.forEach(a => {
          if (!a || a.IsApplicable === false || a.IsApplicable === "false" || a.IsApplicable === 0 || a.IsApplicable === "0") return;
          const amt = parseFloat(a.Amount) || 0;
          const calc = String(a.Calculation || "").toUpperCase().trim();
          if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
            hourlyAllowances += (amt / 100) * allowanceBase;
          } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
            hourlyAllowances += amt / stdHours;
          } else {
            // Fixed monthly amount -> hourly rate is amt / divisor / stdHours
            hourlyAllowances += divisor > 0 ? (amt / divisor / stdHours) : 0;
          }
        });
        baseRate = basicSalary + hourlyAllowances;
      } else {
        // Monthly rate (type 1 or default): Basic is monthly. Calculate monthly allowances rate
        let monthlyAllowances = 0;
        effectiveAllowances.forEach(a => {
          if (!a || a.IsApplicable === false || a.IsApplicable === "false" || a.IsApplicable === 0 || a.IsApplicable === "0") return;
          const amt = parseFloat(a.Amount) || 0;
          const calc = String(a.Calculation || "").toUpperCase().trim();
          if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
            monthlyAllowances += (amt / 100) * allowanceBase;
          } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
            monthlyAllowances += amt * divisor;
          } else {
            // Fixed monthly amount
            monthlyAllowances += amt;
          }
        });
        baseRate = basicSalary + monthlyAllowances;
      }
    } else {
      // Fallback if numeric allowances total was passed
      const numAllowances = parseFloat(allowancesOrTotal) || 0;
      if (numAllowances > 0) {
        if (type === 2) {
          const dailyAl = numAllowances > (basicSalary * 2) && divisor > 0
            ? (numAllowances / divisor)
            : numAllowances;
          baseRate = basicSalary + dailyAl;
        } else if (type === 3) {
          const hourlyAl = numAllowances > (basicSalary * 10) && divisor > 0
            ? (numAllowances / divisor / stdHours)
            : numAllowances;
          baseRate = basicSalary + hourlyAl;
        } else {
          baseRate = basicSalary + numAllowances;
        }
      }
    }
  }

  let hourlyRate = 0;
  if (type === 2) hourlyRate = baseRate / stdHours;
  else if (type === 3) hourlyRate = baseRate;
  else hourlyRate = baseRate / divisor / stdHours;
  return Math.round(hourlyRate * multiplier * hours * 100) / 100;
};

const getMonthIdx = (m) => {
  if (!m) return new Date().getMonth();
  const idx = MONTH_NAMES.findIndex(name => name.toLowerCase() === String(m).toLowerCase());
  return idx >= 0 ? idx : (parseInt(m, 10) - 1 || 0);
};

// PF: employee (PF), employer-A1 (EmplrPF), pension (Pension), employer-only charges (AdminCharges, AC21, AC22)
export const computePF = (pfBaseWages, pf, pfAllowancesAmount = 0, isPensionApplicable = true, pparms = null, options = {}) => {
  if (!pf) return { PF: 0, EmplrPF: 0, Pension: 0, AdminCharges: 0, AC21: 0, AC22: 0 };
  const rate        = parseFloat(pf.Rate)               || 0;
  const erRate      = parseFloat(pf.EmployerRate)       || 0;
  const penRate     = parseFloat(pf.PensionRate)        || 0;
  const maxPen      = parseFloat(pf.MaxPensionAmt)      || 0;
  const maxWagesPF  = parseFloat(pf.MaxWagesForPF)      || 0;
  const maxWagesPen = parseFloat(pf.MaxWagesForPension) || 0;

  const paidDays = options.paidDays !== undefined ? (parseFloat(options.paidDays) || 0) : 30;
  const monthDays = options.monthDays !== undefined ? (parseFloat(options.monthDays) || 30) : 30;
  const X = paidDays >= 0 ? paidDays : 30;
  const Y = monthDays > 0 ? monthDays : 30;
  const proRataMaxPF     = maxWagesPF > 0 ? Math.round((maxWagesPF / Y) * X) : 0;
  const proRataMaxPen    = maxWagesPen > 0 ? Math.round((maxWagesPen / Y) * X) : 0;
  const proRataMaxPenAmt = maxPen > 0 ? Math.round((maxPen / Y) * X) : 0;

  // 1. PFBasis Check
  // If PFBasis is TRUE (ON): Calculate strictly on Basic Wages only (allowances excluded).
  // If PFBasis is FALSE (OFF): Include PF Allowances (or total allowances if pfAllowancesAmount is 0).
  const isPFBasisOnly = parseBoolFlag(pf.PFBasis, false);
  const effectivePFAllowances = pfAllowancesAmount > 0
    ? pfAllowancesAmount
    : (parseFloat(options.totalAllowances) || 0);
  let pfBase = isPFBasisOnly ? pfBaseWages : (pfBaseWages + effectivePFAllowances);

  // 2. New Labour Code 50% Rule & Allowance Adjustment
  const periodMonth = options.selectedMonth;
  const periodYear = options.selectedYear;
  if (pf.NewLabourCodeApplyDate && periodMonth && periodYear) {
    try {
      const lCodeDate = new Date(pf.NewLabourCodeApplyDate);
      const mIdx = getMonthIdx(periodMonth);
      const yNum = parseInt(periodYear, 10);
      const pDate = new Date(yNum, mIdx, 1);
      const minBasicPrec = parseFloat(pf.NewLabourCodeBasicPrec) || 50.0;

      if (pDate >= lCodeDate && minBasicPrec > 0) {
        const totalGross = options.gross !== undefined && options.gross !== null
          ? (parseFloat(options.gross) || 0)
          : (pfBaseWages + (parseFloat(options.totalAllowances) || 0));
        const minWageFloor = Math.round((totalGross * minBasicPrec) / 100);
        if (pfBase < minWageFloor) {
          pfBase = minWageFloor;
        }
      }
    } catch (_) {}
  }

  const fixedPFSal = parseFloat(options.emp?.FixedPFSalary) || 0;
  const actualWages = fixedPFSal > 0 ? fixedPFSal : pfBase;

  // 3. Employee PF Wages & Overlimit Check
  // Note: If Higher Pension is active, Employee contribution must also expand to actual wages per EPFO Joint Option rules
  const isHigherPen = options.isHigherPen !== undefined ? !!options.isHigherPen : !!(options.emp?.HigherPension);
  const isEeOverLimit = options.isEeOverLimit !== undefined ? !!options.isEeOverLimit : !!(options.emp?.EmpleeDedOverLimit || isHigherPen);
  const eePFWages = (isEeOverLimit || isHigherPen) ? actualWages : (proRataMaxPF > 0 ? Math.min(actualWages, proRataMaxPF) : actualWages);
  let empPF = Math.round(eePFWages * rate / 100);

  // Extra PF (VPF)
  const extraPF = parseFloat(options.emp?.ExtraPF) || 0;
  if (extraPF > 0) {
    if (String(options.emp?.PFCalculationType || "").toLowerCase() === "percentage") {
      empPF += Math.round((eePFWages * extraPF) / 100);
    } else {
      empPF += Math.round(extraPF);
    }
  }

  // 4. Employer PF Wages & Overlimit Check
  const isErOverLimit = options.isErOverLimit !== undefined ? !!options.isErOverLimit : !!(options.emp?.EmplrPFOverLimit || isHigherPen);
  const erPFWages = (isErOverLimit || isHigherPen)
    ? actualWages
    : (proRataMaxPF > 0 ? Math.min(actualWages, proRataMaxPF) : actualWages);
  const totalEmployerContr = Math.round(erPFWages * erRate / 100);

  // 5. Age 58 Cut-off for EPS
  let isRetiredFromEPS = false;
  if (options.emp?.DateOfBirth) {
    try {
      const dob = new Date(options.emp.DateOfBirth);
      const yNum = parseInt(periodYear, 10) || new Date().getFullYear();
      const mIdx = getMonthIdx(periodMonth);
      let age = yNum - dob.getFullYear();
      if (mIdx < dob.getMonth()) age--;
      const retAge = parseFloat(pparms?.RetirementAge) || 58;
      if (age >= retAge && !options.emp?.IsDeferredPension) {
        isRetiredFromEPS = true;
      }
    } catch (_) {}
  }

  const penApply = isPensionApplicable !== false &&
    isPensionApplicable !== 0 &&
    isPensionApplicable !== "0" &&
    String(isPensionApplicable).toLowerCase() !== "false" &&
    Boolean(isPensionApplicable) &&
    !isRetiredFromEPS;

  let pension = 0;
  if (penApply && penRate > 0) {
    const isPenOverLimit = options.isPenOverLimit !== undefined ? !!options.isPenOverLimit : !!(options.emp?.PensionOverLimit || isHigherPen);
    const penWages = (isPenOverLimit || isHigherPen)
      ? actualWages
      : (maxWagesPen > 0 ? Math.min(actualWages, maxWagesPen) : actualWages);

    pension = Math.round(penWages * penRate / 100);
    if (!isPenOverLimit && !isHigherPen && maxPen > 0) {
      pension = Math.min(pension, maxPen);
    }

    const higherPenThreshold = maxWagesPen > 0 ? maxWagesPen : 15000;
    if (isHigherPen && higherPenThreshold > 0 && penWages > higherPenThreshold) {
      const additionalPen = Math.round(((penWages - higherPenThreshold) * 1.16) / 100);
      pension += additionalPen;
    }
    pension = Math.min(pension, totalEmployerContr);
  }

  let emplrPF = Math.max(totalEmployerContr - pension, 0);

  const isFullPF = !!(pparms?.IsFullPFEmployer || pparms?.FullPFByEmployer || String(pparms?.IsFullPFEmployer).toLowerCase() === "true" || pparms?.IsFullPFEmployer === 1);
  if (isFullPF && empPF > 0) {
    emplrPF += empPF;
    empPF = 0;
  }

  // Admin Charges (employer only)
  const adminRate  = parseFloat(pf.AdminCharges)          || 0;
  const adminMin   = parseFloat(pf.AdminChargesMinAmount) || 0;
  const AdminCharges = Math.max(Math.round(erPFWages * adminRate / 100 * 100) / 100, adminMin);

  // AC21 (EDLI) - strictly capped at pro-rata ₹75
  const isEDLIFixed    = !!(pparms?.AC2122OnFixedMaxPFWages);
  const edliWages      = isEDLIFixed && proRataMaxPF > 0 ? Math.min(erPFWages, proRataMaxPF) : erPFWages;
  const ac21WithGrp    = parseFloat(pf.AC21WithGroupIns)         || 0;
  const ac21WithoutGrp = parseFloat(pf.AC21WithoutGroupIns)      || 0;
  const ac21MinAmt     = parseFloat(pf.AC21WithGroupInsMinAmt)   || 0;
  const ac21Rate       = ac21WithGrp > 0 ? ac21WithGrp : ac21WithoutGrp;
  const ac21Calc       = Math.round(edliWages * ac21Rate / 100 * 100) / 100;
  const maxEDLIAmt     = Y > 0 ? (75.0 / Y) * X : 75.0;
  const AC21           = Math.min(Math.max(ac21Calc, ac21MinAmt), maxEDLIAmt);

  // AC22
  const ac22Rate = (parseFloat(pf.AC22WithGroupIns) || 0) > 0 ? parseFloat(pf.AC22WithGroupIns) : (parseFloat(pf.AC22WithoutGroupIns) || 0);
  const ac22Min  = parseFloat(pf.AC22WithGroupInsMinAmt) || 0;
  const AC22     = ac22Rate > 0 ? Math.max(Math.round(erPFWages * ac22Rate / 100 * 100) / 100, ac22Min) : 0;

  return { PF: empPF, EmplrPF: emplrPF, Pension: pension, AdminCharges, AC21, AC22 };
};

// ── Arrear PF, Employer PF & Pension Calculation ───────────────────────────
// Employee Share: 12% on Arrear -> ArrearPF
// Employer Share: 12% on Arrear -> Split into Pension (ArrPens 8.33%) + EPF (ArrERPF 3.67%)
// If Pension + Normal Pension exceeds Statutory Ceiling Limit, ceiling check / validation applies.
export const computeArrearPF = (arrearVal, normalPension = 0, pfRate = null, isPensionApplicable = true, pparms = null, options = {}) => {
  const arrear = parseFloat(arrearVal) || 0;
  if (arrear <= 0 || !pfRate) {
    return {
      ArrearPF: 0,
      ArrERPF: 0,
      ArrPens: 0,
      TotalEmployerArrear: 0,
      NormalPension: parseFloat(normalPension) || 0,
      FullArrPens: 0,
      CappedArrPens: 0,
      CombinedPension: parseFloat(normalPension) || 0,
      PensionCeilingLimit: 0,
      IsExceeding: false,
    };
  }

  const rate        = parseFloat(pfRate.Rate)               || 12;
  const erRate      = parseFloat(pfRate.EmployerRate)       || 12;
  const penRate     = parseFloat(pfRate.PensionRate)        || 8.33;
  const maxWagesPen = parseFloat(pfRate.MaxWagesForPension) || 0;
  const maxPenAmt   = parseFloat(pfRate.MaxPensionAmt)      || 0;

  // Pension statutory ceiling limit: e.g. 2083 (25000 * 8.33%) or 1250 (15000 * 8.33%)
  const pensionCeilingLimit = maxPenAmt > 0
    ? maxPenAmt
    : (maxWagesPen > 0 ? Math.round((maxWagesPen * penRate) / 100) : 2083);

  // Age 58 Cut-off for EPS
  let isRetiredFromEPS = false;
  if (options.emp?.DateOfBirth) {
    try {
      const dob = new Date(options.emp.DateOfBirth);
      const yNum = parseInt(options.selectedYear, 10) || new Date().getFullYear();
      const mIdx = getMonthIdx(options.selectedMonth);
      let age = yNum - dob.getFullYear();
      if (mIdx < dob.getMonth()) age--;
      const retAge = parseFloat(pparms?.RetirementAge) || 58;
      if (age >= retAge && !options.emp?.IsDeferredPension) {
        isRetiredFromEPS = true;
      }
    } catch (_) {}
  }

  const penApply = isPensionApplicable !== false &&
    isPensionApplicable !== 0 &&
    isPensionApplicable !== "0" &&
    String(isPensionApplicable).toLowerCase() !== "false" &&
    Boolean(isPensionApplicable) &&
    !isRetiredFromEPS;

  const arrearPF            = Math.round((arrear * rate) / 100);
  const totalEmployerArrear = Math.round((arrear * erRate) / 100);
  const normalPen           = parseFloat(normalPension) || 0;
  const fullArrPens         = penApply ? Math.round((arrear * penRate) / 100) : 0;
  const combinedPension     = normalPen + fullArrPens;
  const cappedArrPens       = penApply ? Math.max(0, pensionCeilingLimit - normalPen) : 0;
  const isExceeding         = penApply && pensionCeilingLimit > 0 && (combinedPension > pensionCeilingLimit);

  const isHigherPen = !!(options.isHigherPen || options.emp?.HigherPension || options.emp?.PensionOverLimit);
  let finalArrPens = 0;
  if (penApply) {
    if (options.choice === 'full' || isHigherPen) {
      finalArrPens = fullArrPens;
    } else if (options.choice === 'capped') {
      finalArrPens = cappedArrPens;
    } else if (!isExceeding) {
      finalArrPens = fullArrPens;
    } else {
      finalArrPens = cappedArrPens;
    }
  }
  const finalArrERPF = Math.max(0, totalEmployerArrear - finalArrPens);

  return {
    ArrearPF: arrearPF,
    ArrERPF: finalArrERPF,
    ArrPens: finalArrPens,
    TotalEmployerArrear: totalEmployerArrear,
    NormalPension: normalPen,
    FullArrPens: fullArrPens,
    CappedArrPens: cappedArrPens,
    CombinedPension: combinedPension,
    PensionCeilingLimit: pensionCeilingLimit,
    IsExceeding: isExceeding,
  };
};

// ESI: employee (ESI) + employer (EmplrESI)
// Statutory Rules & Logic:
// 1. Statutory Wage Limit: ₹21,000 for regular employees; ₹25,000 for Persons with Disabilities (IsHandicap).
// 2. OT Duality: Overtime is excluded when evaluating the wage ceiling (₹21,000 / ₹25,000) for coverage eligibility.
//    If regular wages <= ceiling, employee is eligible. When eligible, if IsESIOnOT is ON, OT is included in contribution base.
// 3. Daily Wage Exemption (Section 42(1)): If Average Daily Wage (taxableGross / paidDays) <= ₹176,
//    Employee contribution is EXEMPT (₹0), while Employer contribution (3.25%) is still payable.
// 4. New Labour Code (Code on Social Security 2020 / Code on Wages 2019):
//    If NewLabourCodeApplyDate is active, core wage base cannot be less than 50% (NewLabourCodeBasicPrec) of Total Gross.
// 5. Overlimit & Contribution Period Lock-in (Rule 50 ESIC Rules):
//    - Contribution periods: 1 April - 30 Sept, and 1 Oct - 31 March.
//    - If employee was covered (wages <= ₹21,000 / ₹25,000) at the start of contribution period or on joining,
//      they remain covered until the end of the 6-month contribution period even if wages cross limit.
//    - Contribution is deducted on FULL actual gross wages (no ₹21,000 capping exists in ESI Act).
//    - If employee started above ceiling and not covered under Rule 50: Excluded Employee (ESI = 0, EmplrESI = 0).
// 6. Full ESI by Employer:
//    If IsFullESIEmployer / FullESIByEmployer is ON: Employer pays full 4% (3.25% + 0.75%), Employee pays 0%.
// 7. Rounding (Regulation 31C - Next Higher Rupee):
//    Under Regulation 31C, contributions are rounded up to the NEXT HIGHER RUPEE (Math.ceil).
export const getESIContributionPeriod = (monthName) => {
  const m = String(monthName || "").trim().toLowerCase();
  const cycle1 = ["april", "may", "june", "july", "august", "september", "apr", "jun", "jul", "aug", "sep"];
  const cycle2 = ["october", "november", "december", "january", "february", "march", "oct", "nov", "dec", "jan", "feb", "mar"];
  if (cycle1.includes(m)) return { cycle: 1, name: "April - September" };
  if (cycle2.includes(m)) return { cycle: 2, name: "October - March" };
  return { cycle: 0, name: "" };
};

export const computeESI = (grossWages, esi, pparms, options = {}) => {
  if (options.isESIApplicable === false || options.isESIApplicable === 0 || options.isESIApplicable === "0" || String(options.isESIApplicable).toLowerCase() === "false") {
    return { ESI: 0, EmplrESI: 0, IsOverLimit: false, IsExempt: false, TaxableGross: 0, WagesLimit: 21000, AvgDailyWage: 0, Tooltip: "ESI Not Applicable" };
  }

  const emp = options.emp || null;
  const isHandicap = !!(
    emp?.IsHandicap === true ||
    emp?.IsHandicap === 1 ||
    emp?.IsHandicap === "1" ||
    String(emp?.IsHandicap).toLowerCase() === "true" ||
    options.isHandicap === true ||
    options.isHandicap === 1 ||
    options.isHandicap === "1" ||
    String(options.isHandicap).toLowerCase() === "true"
  );
  const statutoryLimit = isHandicap ? 25000 : 21000;
  let wagesLimit = (esi && n(esi.WagesLimit) > 0) ? n(esi.WagesLimit) : statutoryLimit;
  if (isHandicap && wagesLimit < 25000) {
    wagesLimit = 25000;
  }
  const empRate = (esi && esi.EmployeeContribution !== undefined && esi.EmployeeContribution !== "") ? n(esi.EmployeeContribution) : 0.75;
  const erRate = (esi && esi.EmployerContribution !== undefined && esi.EmployerContribution !== "") ? n(esi.EmployerContribution) : 3.25;
  const exemptionLimit = (esi && esi.EmployeeExemptionLimit !== undefined && esi.EmployeeExemptionLimit !== "") ? n(esi.EmployeeExemptionLimit) : 176;
  const roundOn = esi ? !!(esi?.RoundedOn) : true;

  const paidDays = options.paidDays !== undefined ? (parseFloat(options.paidDays) || 0) : 30;
  const otAmount = parseFloat(options.otAmount) || 0;
  const isESIOnOT = !!(pparms?.IsESIOnOT);
  
  // Overtime Duality Rule: Overtime is excluded when verifying eligibility ceiling (Section 2(22))
  const regularWages = Math.max(0, grossWages - (isESIOnOT ? otAmount : 0));

  let taxableGross = grossWages;

  // New Labour Code 50% Rule & Allowance Adjustment
  const periodMonth = options.selectedMonth;
  const periodYear = options.selectedYear;
  let isLabourCodeApplied = false;
  if (esi?.NewLabourCodeApplyDate && periodMonth && periodYear) {
    try {
      const lCodeDate = new Date(esi.NewLabourCodeApplyDate);
      const mIdx = getMonthIdx(periodMonth);
      const yNum = parseInt(periodYear, 10);
      const pDate = new Date(yNum, mIdx, 1);
      const minBasicPrec = parseFloat(esi.NewLabourCodeBasicPrec) || 50.0;

      if (pDate >= lCodeDate && minBasicPrec > 0) {
        const totalGross = options.totalGross !== undefined && options.totalGross !== null
          ? (parseFloat(options.totalGross) || 0)
          : taxableGross;
        const minWageFloor = Math.round((totalGross * minBasicPrec) / 100);
        if (taxableGross < minWageFloor) {
          taxableGross = minWageFloor;
          isLabourCodeApplied = true;
        }
      }
    } catch (_) {}
  }

  // Statutory Rule 50: 6-Month Contribution Period Lock-in
  // If an employee was eligible (regular wages <= limit) at start of cycle (April/October) or on joining,
  // they remain covered on full gross wages till the cycle ends.
  const isRule50Covered = !!(
    options.isContributionPeriodLocked ||
    options.isRule50Eligible ||
    options.isMidCycleEligible ||
    (options.startOfCycleWage !== undefined && options.startOfCycleWage <= wagesLimit) ||
    (emp && (parseFloat(emp.BasicSalary) || 0) <= wagesLimit)
  );

  const isOverLimit = wagesLimit > 0 && regularWages > wagesLimit;
  let isLockedInCycle = false;

  if (isOverLimit) {
    const isOverLimitOn = !!(
      pparms?.IsOverLimitESI ||
      pparms?.OverLimitESI ||
      String(pparms?.IsOverLimitESI).toLowerCase() === "true" ||
      pparms?.IsOverLimitESI === 1
    );

    if (isRule50Covered || isOverLimitOn) {
      // In ESI, if covered, contribution is calculated on FULL actual gross wages! No ₹21,000 cap.
      taxableGross = grossWages;
      isLockedInCycle = true;
    } else {
      // Excluded employee from inception/start of period: ESI is 0
      taxableGross = 0;
    }
  }

  if (taxableGross <= 0) {
    return {
      ESI: 0,
      EmplrESI: 0,
      IsOverLimit: true,
      IsRule50Covered: false,
      IsExempt: false,
      TaxableGross: 0,
      WagesLimit: wagesLimit,
      AvgDailyWage: 0,
      Tooltip: `Salary (₹${regularWages.toLocaleString()}) exceeds statutory ESI limit of ₹${wagesLimit.toLocaleString()} (Excluded Employee)`
    };
  }

  // Daily Wage Exemption Check (Section 42(1): Avg daily wage <= ₹176)
  // Only applies when employee has actual paid days (paidDays > 0)
  const avgDailyWage = paidDays > 0 ? Math.round((taxableGross / paidDays) * 100) / 100 : 0;
  const isDailyWageExempt = paidDays > 0 && exemptionLimit > 0 && avgDailyWage <= exemptionLimit;

  let empESI = isDailyWageExempt ? 0 : (taxableGross * empRate / 100);
  let emplrESI = taxableGross * erRate / 100;

  // Full ESI by Employer parameter support (Employer pays 4.00%, Employee pays 0%)
  const isFullESI = !!(
    pparms?.IsFullESIEmployer ||
    pparms?.FullESIByEmployer ||
    String(pparms?.IsFullESIEmployer).toLowerCase() === "true" ||
    pparms?.IsFullESIEmployer === 1
  );
  if (isFullESI) {
    emplrESI = taxableGross * (empRate + erRate) / 100;
    empESI = 0;
  }

  // Regulation 31C Rounding: Round off to the NEXT HIGHER RUPEE (Math.ceil)
  if (roundOn) {
    empESI = Math.ceil(empESI);
    emplrESI = Math.ceil(emplrESI);
  } else {
    empESI = Math.round(empESI * 100) / 100;
    emplrESI = Math.round(emplrESI * 100) / 100;
  }

  let tooltip = `ESI Gross: ₹${taxableGross.toLocaleString()}`;
  if (isDailyWageExempt) {
    tooltip += ` | Avg daily wage ₹${avgDailyWage} <= ₹${exemptionLimit}: Employee Exempt (0%), Employer ${isFullESI ? (empRate + erRate) : erRate}%`;
  } else if (isFullESI) {
    tooltip += ` | Full ESI Paid by Employer (4.00%)`;
  } else if (isLockedInCycle) {
    tooltip += ` | Rule 50 Lock-in Active: Gross ₹${taxableGross.toLocaleString()} (Wage crossed limit mid-cycle, covered till cycle end)`;
  } else if (isHandicap) {
    tooltip += ` | PWD ceiling ₹${wagesLimit.toLocaleString()}`;
  } else if (isLabourCodeApplied) {
    tooltip += ` | 50% New Labour Code floor applied`;
  }

  return {
    ESI: empESI,
    EmplrESI: emplrESI,
    IsOverLimit: isOverLimit,
    IsRule50Covered: isLockedInCycle,
    IsExempt: isDailyWageExempt,
    TaxableGross: taxableGross,
    WagesLimit: wagesLimit,
    AvgDailyWage: avgDailyWage,
    Tooltip: tooltip
  };
};

// Bonus
export const computeBonus = (wages, bonus, workingDays, paidDays, pparms) => {
  if (!bonus) return 0;
  if (pparms && pparms.IsBonusMonthly === false) return 0;
  const rate     = parseFloat(bonus.BonusRate)        || 0;
  const maxWages = parseFloat(bonus.MaximumWages)     || 0;
  const maxBonus = parseFloat(bonus.MaximumBonus)     || 0;
  const minBonus = parseFloat(bonus.MinimumBonus)     || 0;
  const minDays  = parseFloat(bonus.MinimumBonusDays) || 0;
  const daysForBonus = (pparms?.IsBonusOnWorkingDays) ? workingDays : paidDays;
  if (minDays > 0 && daysForBonus < minDays) return 0;

  const minWagesParam = parseFloat(pparms?.MinWages) || 0;
  let baseWages = (pparms?.IsBonusOnMinWages && minWagesParam > 0) ? minWagesParam : wages;
  const bonusWages = maxWages > 0 ? Math.min(baseWages, maxWages) : baseWages;
  let bonusAmount  = bonusWages * rate / 100;
  if (maxBonus > 0) bonusAmount = Math.min(bonusAmount, maxBonus);
  if (minBonus > 0) bonusAmount = Math.max(bonusAmount, minBonus);
  return Math.round(bonusAmount * 100) / 100;
};

// Gratuity
export const computeGratuity = (basicSalary, gratuity, yearsOfService = 1, pparms) => {
  if (!gratuity) return 0;
  const rate = parseFloat(gratuity.Rate) || 0;
  if (rate > 0) return Math.round(basicSalary * rate / 100 * 100) / 100;
  const payableDays = parseFloat(pparms?.GratuityPayableDays) || 15;
  const years = parseFloat(yearsOfService) || 1;
  return Math.round((basicSalary * payableDays * years / 26) * 100) / 100;
};

export const reconstructAllowancesFromRecord = (record, allowanceMasterList) => {
  const list = [];
  for (let i = 1; i <= 10; i++) {
    const name = record[`Allowance${i}Name`];
    const amt = parseFloat(record[`OriginalAllowance${i}`]) || 0;
    if (name) {
      const master = allowanceMasterList.find(m => m.Name === name);
      if (master) {
        list.push({
          F_AllowanceMaster: master.Id,
          Amount: amt,
          IsApplicable: true,
          IsPFApply: true,
          IsESIApply: true,
          IsRoundOffApply: true,
          IsWorkingDaysApply: false,
        });
      }
    }
  }
  return list;
};

export const reconstructDeductionsFromRecord = (record, deductionMasterList) => {
  const list = [];
  for (let i = 1; i <= 10; i++) {
    const name = record[`Deduction${i}Name`];
    const amt = parseFloat(record[`OriginalDeduction${i}`]) || 0;
    if (name) {
      const master = deductionMasterList.find(m => m.Name === name);
      if (master) {
        list.push({
          F_DeductionMaster: master.Id,
          Amount: amt,
          IsApplicable: true,
        });
      }
    }
  }
  return list;
};

// ─── Full row recompute ───────────────────────────────────────────────────────
export const recomputeRow = (row, allowances, deductions, pfRate, esiRate, bonusRate, gratuityRate, payrollParams, salaryEntry, selectedMonth, selectedYear, allowanceMasterList = [], deductionMasterList = [], departmentListOrObj = null) => {
  const is26Days = is26DaysForEmployee(payrollParams, row);
  const isNoWHForDaily = !!(payrollParams?.IsNoWeeklyHolidayDailyWages || payrollParams?.NoWeeklyHolidayForDailyWages);
  const isDaily = isDailyWageEmployee(row, salaryEntry);
  const effectiveWeeklyHoliday = (is26Days || (isNoWHForDaily && isDaily)) ? 0 : n(row.WeeklyHoliday);

  const monthDays    = n(row.MonthDays) || (is26Days ? 26 : 31);
  const paidDays     = Math.min(monthDays, n(row.WorkingDays) + n(row.LeaveWages) + n(row.FestivalHoliday) + effectiveWeeklyHoliday + n(row.CL) + n(row.ML));
  const workingDays  = computeWorkingDays(monthDays, payrollParams, row);
  const baseSalary   = n(row.BasicSalary) || computeBasic(salaryEntry, workingDays, payrollParams);
  const isDirectWages = isDirectWagesForEmployee(payrollParams, row, departmentListOrObj);
  const wages        = (isDirectWages && n(row.Wages) > 0)
    ? n(row.Wages)
    : computeWages({ BasicSalary: n(row.BasicSalary), F_BasicTypeMaster: salaryEntry?.F_BasicTypeMaster || 1 }, paidDays, monthDays, n(row.WorkingHours), n(row.WorkingMinutes), payrollParams);
  const allowanceBaseSalary = (payrollParams?.IsAllowanceOnArrear && n(row.Arrear) > 0)
    ? (baseSalary + n(row.Arrear))
    : baseSalary;

  const totalAl      = computeAllowancesTotal(allowances, monthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
  const totalDed     = computeDeductionsTotal(deductions, monthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
  const otAmount     = computeOvertime(salaryEntry, n(row.OvertimeHours), monthDays, payrollParams, allowances, selectedMonth, selectedYear, allowanceBaseSalary);
  const pfAlAmt      = computePFAllowancesAmount(allowances, monthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
  const esiAlAmt     = computeESIAllowancesAmount(allowances, monthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
  const bonus        = computeBonus(wages, bonusRate, n(row.WorkingDays), paidDays, payrollParams);
  const gratuity     = computeGratuity(baseSalary, gratuityRate, 1, payrollParams);

  const pfBase = wages
    + (payrollParams?.IsPFOnOT       ? otAmount        : 0)
    + (payrollParams?.IsPFOnExtraPay  ? n(row.ExtraPay)  : 0)
    + (payrollParams?.IsPFOnExtraPay1 ? n(row.ExtraPay1) : 0);

  const esiBase = wages
    + esiAlAmt
    + (payrollParams?.IsESIOnOT       ? otAmount        : 0)
    + (payrollParams?.IsESIOnArrear   ? n(row.Arrear)   : 0)
    + (payrollParams?.IsESIOnExtraPay  ? n(row.ExtraPay)  : 0)
    + (payrollParams?.IsESIOnExtraPay1 ? n(row.ExtraPay1) : 0);

  const gross = wages + n(row.ExtraPay) + n(row.ExtraPay1) + n(row.AdvanceBonus) + n(row.Arrear) + otAmount + totalAl + bonus;

  const isPensionApplicable = row.PensionApplicable !== undefined && row.PensionApplicable !== null ? row.PensionApplicable : true;
  let { PF, EmplrPF, Pension, AdminCharges, AC21, AC22 } = row.PFApplicable
    ? computePF(pfBase, pfRate, pfAlAmt, isPensionApplicable, payrollParams, {
        isEeOverLimit: row.EmpleeDedOverLimit,
        isErOverLimit: row.EmplrPFOverLimit,
        isPenOverLimit: row.PensionOverLimit,
        isHigherPen: row.HigherPension,
        paidDays,
        monthDays,
        totalAllowances: totalAl,
        gross,
        selectedMonth,
        selectedYear,
        emp: row,
      })
    : { PF: 0, EmplrPF: 0, Pension: 0, AdminCharges: 0, AC21: 0, AC22: 0 };

  // ── Arrear PF, Employer PF & Pension ──
  let ArrearPF = n(row.ArrearPF);
  let ArrERPF  = n(row.ArrERPF);
  let ArrPens  = n(row.ArrPens);

  if (row.PFApplicable && n(row.Arrear) > 0) {
    const arrPFResult = computeArrearPF(row.Arrear, Pension, pfRate, isPensionApplicable, payrollParams, {
      emp: row,
      selectedMonth,
      selectedYear,
      isHigherPen: row.HigherPension || row.PensionOverLimit,
    });
    ArrearPF = ArrearPF > 0 ? ArrearPF : arrPFResult.ArrearPF;
    ArrPens  = (row.ArrPens !== undefined && row.ArrPens !== null && row.ArrPens !== "" && n(row.ArrPens) > 0)
      ? n(row.ArrPens)
      : arrPFResult.ArrPens;
    ArrERPF  = (row.ArrERPF !== undefined && row.ArrERPF !== null && row.ArrERPF !== "" && n(row.ArrERPF) > 0)
      ? n(row.ArrERPF)
      : arrPFResult.ArrERPF;
  } else if (!row.PFApplicable || n(row.Arrear) === 0) {
    ArrearPF = 0;
    ArrERPF  = 0;
    ArrPens  = 0;
  }

  const { ESI, EmplrESI } = row.ESIApplicable
    ? computeESI(esiBase, esiRate, payrollParams, {
        emp: row,
        paidDays,
        monthDays,
        otAmount,
        totalGross: gross,
        selectedMonth,
        selectedYear,
        isESIApplicable: row.ESIApplicable,
      })
    : { ESI: 0, EmplrESI: 0 };

  const deduct = PF + ESI + n(row.Advance) + n(row.Recovery) + n(row.TDS) + ArrearPF + totalDed;

  let net = gross - deduct + n(row.PFRefund);
  if (payrollParams?.IsNetPayRounded) {
    net = Math.round(net);
  } else {
    net = Math.round(net * 100) / 100;
  }

  const updatedRow = {
    ...row,
    WeeklyHoliday: effectiveWeeklyHoliday,
    PaidDay: paidDays,
    Wages: wages,
    OverTime: otAmount,
    TotalAllowances: totalAl,
    TotalDeductions: totalDed,
    GrossAmount:   Math.round(gross  * 100) / 100,
    PF, EmplrPF, Pension, AdminCharges, AC21, AC22,
    ArrearPF, ArrERPF, ArrPens,
    ESI, EmplrESI,
    Bonus:    bonus,
    Gratuity: gratuity,
    TotalDeduction: Math.round(deduct * 100) / 100,
    NetAmount: net,
    Original_BasicSalary: n(row.BasicSalary) || baseSalary,
    Calculated_BasicSalary: wages,
  };

  const effectiveAllowances = getEffectiveAllowances(allowances, selectedMonth, selectedYear);
  for (let i = 0; i < 10; i++) {
    const a = effectiveAllowances[i];
    if (a) {
      const computed = computeSingleAllowance(a, monthDays, paidDays, baseSalary);
      updatedRow[`Allowance${i+1}Name`] = allowanceMasterList.find(m => String(m.Id) === String(a.F_AllowanceMaster))?.Name || `Allow_${a.F_AllowanceMaster}`;
      updatedRow[`OriginalAllowance${i+1}`] = parseFloat(a.Amount) || 0;
      updatedRow[`CalculatedAllowance${i+1}`] = computed;
    } else {
      updatedRow[`Allowance${i+1}Name`] = "";
      updatedRow[`OriginalAllowance${i+1}`] = 0;
      updatedRow[`CalculatedAllowance${i+1}`] = 0;
    }
  }

  const effectiveDeductions = getEffectiveDeductions(deductions, selectedMonth, selectedYear);
  for (let i = 0; i < 10; i++) {
    const d = effectiveDeductions[i];
    if (d) {
      const computed = computeSingleDeduction(d, monthDays, paidDays, baseSalary);
      updatedRow[`Deduction${i+1}Name`] = deductionMasterList.find(m => String(m.Id) === String(d.F_DeductionMaster))?.Name || `Deduct_${d.F_DeductionMaster}`;
      updatedRow[`OriginalDeduction${i+1}`] = parseFloat(d.Amount) || 0;
      updatedRow[`CalculatedDeduction${i+1}`] = computed;
    } else {
      updatedRow[`Deduction${i+1}Name`] = "";
      updatedRow[`OriginalDeduction${i+1}`] = 0;
      updatedRow[`CalculatedDeduction${i+1}`] = 0;
    }
  }

  return updatedRow;
};

// ─── Employee Sorting based on PayrollParameter ─────────────────────────────
export const sortEmployeesByParams = (employees, pparms) => {
  if (!employees || !Array.isArray(employees)) return [];

  const naturalCompare = (a, b) => {
    return String(a || "").localeCompare(String(b || ""), undefined, { numeric: true, sensitivity: 'base' });
  };

  return [...employees].sort((a, b) => {
    // 1. Department-wise grouping if enabled
    if (pparms?.IsDepartmentWisePayroll) {
      const deptA = (a.DepartmentName || a.Department || "").toLowerCase().trim();
      const deptB = (b.DepartmentName || b.Department || "").toLowerCase().trim();
      if (deptA !== deptB) {
        return deptA.localeCompare(deptB);
      }
    }

    // 2. Ordering criteria (ASC)
    if (pparms?.IsPFNoWise) {
      const pfA = (a.PFNo || "").trim();
      const pfB = (b.PFNo || "").trim();
      if (pfA && pfB) {
        const cmp = naturalCompare(pfA, pfB);
        if (cmp !== 0) return cmp;
      } else if (pfA && !pfB) {
        return -1;
      } else if (!pfA && pfB) {
        return 1;
      }
      const codeA = a.EmployeeCode || a.CardNo || a.SerialNo || a.Id || "";
      const codeB = b.EmployeeCode || b.CardNo || b.SerialNo || b.Id || "";
      const codeCmp = naturalCompare(codeA, codeB);
      if (codeCmp !== 0) return codeCmp;
      return (a.Name || "").localeCompare(b.Name || "");
    }

    if (pparms?.IsCardNoWise) {
      // EmployeeCode and CardNo are interchangeable
      const cardA = (a.EmployeeCode || a.CardNo || a.SerialNo || a.Id || "").toString().trim();
      const cardB = (b.EmployeeCode || b.CardNo || b.SerialNo || b.Id || "").toString().trim();
      const cmp = naturalCompare(cardA, cardB);
      if (cmp !== 0) return cmp;
      return (a.Name || "").localeCompare(b.Name || "");
    }

    if (pparms?.IsSerialNoWise) {
      const sA = (a.SerialNo || a.EmployeeCode || a.CardNo || a.Id || "").toString().trim();
      const sB = (b.SerialNo || b.EmployeeCode || b.CardNo || b.Id || "").toString().trim();
      const cmp = naturalCompare(sA, sB);
      if (cmp !== 0) return cmp;
      return (a.Name || "").localeCompare(b.Name || "");
    }

    if (pparms?.IsNameWise) {
      const nameA = (a.Name || "").trim().toLowerCase();
      const nameB = (b.Name || "").trim().toLowerCase();
      const cmp = nameA.localeCompare(nameB);
      if (cmp !== 0) return cmp;
      const codeA = a.EmployeeCode || a.CardNo || a.SerialNo || a.Id || "";
      const codeB = b.EmployeeCode || b.CardNo || b.SerialNo || b.Id || "";
      return naturalCompare(codeA, codeB);
    }

    // Default / Fallback: Sort by EmployeeCode / CardNo / SerialNo / Id ASC
    const codeA = a.EmployeeCode || a.CardNo || a.SerialNo || a.Id || "";
    const codeB = b.EmployeeCode || b.CardNo || b.SerialNo || b.Id || "";
    const cmp = naturalCompare(codeA, codeB);
    if (cmp !== 0) return cmp;
    return (a.Name || "").localeCompare(b.Name || "");
  });
};

export const getEmployeeDisplayLabel = (e, pparms) => {
  if (!e) return "";
  const code = String(e.EmployeeCode || e.CardNo || "").trim();
  const sr = e.SerialNo ? `Sr: ${e.SerialNo}` : "";
  const pf = String(e.PFNo || "").trim();
  const name = e.Name || "";

  if (pparms?.IsPFNoWise) {
    return pf ? `[PF: ${pf}] ${name}${code ? ` (${code})` : ""}` : (code ? `[${code}] ${name}` : name);
  }
  if (pparms?.IsSerialNoWise) {
    return sr ? `[${sr}] ${name}${code ? ` (${code})` : ""}` : (code ? `[${code}] ${name}` : name);
  }
  if (pparms?.IsCardNoWise) {
    return code ? `[${code}] ${name}` : (sr ? `[${sr}] ${name}` : name);
  }
  if (pparms?.IsNameWise) {
    return `${name}${code ? ` (${code})` : ""}`;
  }
  return code ? `[${code}] ${name}` : (sr ? `[${sr}] ${name}` : name);
};
